import { ActionIcon, Center, Loader, Tabs, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { isEmpty, isNonNullish, isNullish } from 'remeda';

import { useLoadReminder } from '../api';
import {
  AiCallTab,
  EmailTab,
  FailedReminderStatus,
  PostTab,
  ResendReminderModal,
  SentReminderActions,
} from '../components';

type TabType = 'email' | 'post' | 'aiCall';

export const Reminder = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { id } = useParams<{ id: string }>();

  const { reminder, isReminderLoading } = useLoadReminder(id);

  const [activeTab, setActiveTab] = useState<TabType | null>(null);

  const hasEmail = reminder && !isEmpty(reminder.emails);
  const hasPost = reminder && !isEmpty(reminder.letters);
  const hasAiCall = reminder && isNonNullish(reminder.aiCall?.status);

  useEffect(() => {
    if (isNullish(reminder)) return;

    const defaultActiveTab = (() => {
      if (hasEmail) return 'email';
      if (hasPost) return 'post';
      if (hasAiCall) return 'aiCall';
      return null;
    })();

    setActiveTab(defaultActiveTab);
  }, [reminder, hasEmail, hasPost, hasAiCall]);

  const handleClickBack = () => {
    if (location.pathname.includes('failed')) history.push('/reminders/failed');
    else history.push('/reminders');
  };

  if (isReminderLoading || isNullish(reminder)) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  if (!hasEmail && !hasPost && !hasAiCall) {
    return (
      <>
        <PageTitle>
          <ActionIcon onClick={handleClickBack} size="lg" variant="light">
            <IconArrowLeft stroke={1.5} />
          </ActionIcon>
          {t(i18nKeys.REMINDER.REMINDER)}
        </PageTitle>
        <Text>{t(i18nKeys.REMINDER_LIST.BADGE.MISSING_INFORMATION)}</Text>
      </>
    );
  }

  return (
    <>
      <PageTitle overrideTitle={`${t(i18nKeys.REMINDER.REMINDER)} - ${reminder.debtorFullName}`}>
        {t(i18nKeys.REMINDER.REMINDER)}
        <PageTitle.Back />
        <PageTitle.Actions>
          <FailedReminderStatus reminder={reminder} />
          <SentReminderActions reminder={reminder} />
          <div id="reminder-download-button-portal" />
        </PageTitle.Actions>
      </PageTitle>
      <Tabs value={activeTab} onChange={v => setActiveTab(v as TabType)}>
        <Tabs.List my="lg">
          {hasEmail && <Tabs.Tab value="email">{t(i18nKeys.EMAIL)}</Tabs.Tab>}
          {hasPost && <Tabs.Tab value="post">{t(i18nKeys.POST)}</Tabs.Tab>}
          {hasAiCall && <Tabs.Tab value="aiCall">{t(i18nKeys.AI_CALL)}</Tabs.Tab>}
        </Tabs.List>
        {(() =>
          ({
            email: (
              <Tabs.Panel keepMounted={false} value="email">
                <EmailTab reminder={reminder} />
              </Tabs.Panel>
            ),
            post: (
              <Tabs.Panel keepMounted={false} value="post">
                <PostTab reminder={reminder} />
              </Tabs.Panel>
            ),
            aiCall: (
              <Tabs.Panel keepMounted={false} value="aiCall">
                <AiCallTab
                  aiCall={reminder.aiCall}
                  debtorId={reminder.debtorId}
                  debtorFullName={reminder.debtorFullName}
                />
              </Tabs.Panel>
            ),
          })[activeTab!])()}
      </Tabs>
      <ResendReminderModal />
    </>
  );
};
